import { useState, useEffect, useMemo } from "react";
import { RefreshCw, RotateCw, RefreshCcw, Undo2, Loader2, ArrowRightLeft, Check, ChevronsUpDown, Package } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MobileCards, DataCard, EmptyCard } from "@/components/DataCards";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { api, apiError } from "@/lib/api";
import { formatMac, isCompleteMac } from "@/lib/mac";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export default function UserActivations() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [refreshingId, setRefreshingId] = useState(null);
  const [appsMap, setAppsMap] = useState({});
  const [resend, setResend] = useState(null); // { act }
  const [selectedAppId, setSelectedAppId] = useState("");
  const [form, setForm] = useState({ mac_app: "", key: "" });
  const [sending, setSending] = useState(false);
  const [appOpen, setAppOpen] = useState(false);
  const limit = 20;

  const load = () => api.get(`/activations/mine?page=${page}&limit=${limit}`).then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, [page]);
  useEffect(() => {
    api.get("/apps").then(({ data }) => {
      const m = {}; data.forEach((a) => { m[a.id] = a; }); setAppsMap(m);
    }).catch(() => {});
  }, []);

  const appsList = useMemo(
    () => Object.values(appsMap).sort((a, b) => (a.order - b.order) || String(a.name).localeCompare(String(b.name))),
    [appsMap]
  );

  const refreshOne = async (id) => {
    setRefreshingId(id);
    try {
      const { data: updated } = await api.post(`/activations/${id}/refresh`);
      setData((d) => ({ ...d, items: d.items.map((a) => (a.id === id ? updated : a)) }));
      toast.success("Status atualizado");
    } catch (e) { toast.error(apiError(e)); } finally { setRefreshingId(null); }
  };

  const openResend = (act, openPicker = false) => {
    const app = appsMap[act.app_id];
    if (!app) return toast.error("Este aplicativo não está mais disponível.");
    setResend({ act });
    setSelectedAppId(act.app_id);
    setForm({ mac_app: app.needs_device_id ? (act.mac_app || "") : formatMac(act.mac_app || ""), key: act.key || "" });
    setAppOpen(openPicker);
  };

  const selApp = appsMap[selectedAppId];
  const switching = resend && selectedAppId !== resend.act.app_id;
  const paidWith = resend?.act?.paid_with || ((resend?.act?.credits_charged || 0) > 0 ? "credits" : "credits");
  const isCredits = paidWith === "credits";
  const oldVal = isCredits ? Number(resend?.act?.credits_charged ?? 0) : Number(resend?.act?.paid_value ?? 0);
  const newVal = isCredits ? Number(selApp?.credit_cost ?? 0) : Number(selApp?.price ?? 0);
  const diff = Math.round((newVal - oldVal) * 100) / 100;
  const money = (v) => `R$ ${Number(v).toFixed(2)}`;
  const unitVal = (v) => (isCredits ? `${v} crédito(s)` : money(v));

  const submitResend = async () => {
    const { act } = resend;
    if (!form.mac_app.trim()) return toast.error("Informe o dado de ativação.");
    if (!selApp?.needs_device_id && !isCompleteMac(form.mac_app)) return toast.error("Informe um MAC válido (ex: 49:78:2d:63:87:d9).");
    if (selApp?.needs_mac_key && !form.key.trim()) return toast.error("Informe a Chave (KEY).");
    setSending(true);
    try {
      const payload = { mac_app: form.mac_app.trim(), key: form.key.trim() || null };
      if (switching) payload.app_id = selectedAppId;
      const { data: updated } = await api.post(`/activations/${act.id}/resend`, payload);
      setData((d) => ({ ...d, items: d.items.map((a) => (a.id === act.id ? updated : a)) }));
      const msg = updated.status === "approved" ? "Ativação aprovada!" : updated.status === "pending" ? "Reenviado! Aguardando confirmação." : "Não foi possível ativar.";
      updated.status === "failed" ? toast.error(msg) : toast.success(msg);
      setResend(null);
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const macLabel = selApp?.needs_device_id ? "ID do Dispositivo" : "Endereço MAC";

  return (
    <div className="space-y-6" data-testid="user-activations">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Minhas ativações</h2>
        <Button variant="outline" size="sm" className="gap-2 rounded-full transition-all hover:shadow-sm" onClick={load} data-testid="activations-reload-btn">
          <RefreshCw className="h-4 w-4" /> Atualizar lista
        </Button>
      </div>
      <MobileCards testid="act-cards">
        {data.items.length === 0 && <EmptyCard>Nenhuma ativação encontrada.</EmptyCard>}
        {data.items.map((a) => (
          <DataCard
            key={a.id}
            testid={`act-card-${a.id}`}
            leading={
              <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
                {appsMap[a.app_id]?.logo
                  ? <img src={appsMap[a.app_id].logo} alt="" className="h-full w-full object-cover" onError={(e) => (e.target.style.display = "none")} />
                  : <Package className="h-4 w-4 text-muted-foreground" />}
              </span>
            }
            title={a.app_name}
            subtitle={a.mac_app}
            badge={<StatusBadge status={a.status} />}
            rows={[
              { label: "Créditos", value: <span className="font-num">{a.credits_charged}</span> },
              { label: "Data", value: new Date(a.created_at).toLocaleDateString("pt-BR") },
              a.status === "incorrect"
                ? { label: "Aviso", value: <span className="text-orange-600 dark:text-orange-400">Corrija o dado (ou troque o app) e reenvie</span>, full: true, wrap: true }
                : null,
              (a.credits_charged || 0) === 0 && (a.credits_cost || 0) > 0 && ["refunded", "failed"].includes(a.status)
                ? { label: "Aviso", value: <span className="text-green-600 dark:text-green-400">Crédito estornado</span>, full: true }
                : null,
              a.refunded_to === "balance" && (a.refunded_amount || 0) > 0
                ? { label: "Reembolso", value: <span className="text-green-600 dark:text-green-400">R$ {Number(a.refunded_amount).toFixed(2)} em saldo</span>, full: true }
                : null,
              (a.credits_charged || 0) > 0 && a.status === "failed"
                ? { label: "Aviso", value: "Crédito retido — fale com o suporte", full: true, wrap: true }
                : null,
            ]}
            actions={
              a.status === "pending" ? (
                <Button variant="outline" size="sm" className="flex-1 gap-1.5 rounded-full" disabled={refreshingId === a.id} onClick={() => refreshOne(a.id)} data-testid={`act-refresh-m-${a.id}`}>
                  <RotateCw className={`h-4 w-4 ${refreshingId === a.id ? "animate-spin" : ""}`} /> Verificar
                </Button>
              ) : ["incorrect", "failed", "refunded"].includes(a.status) ? (
                <>
                  <Button size="sm" className="flex-1 gap-1.5 rounded-full" onClick={() => openResend(a)} data-testid={`act-retry-m-${a.id}`}>
                    <RefreshCcw className="h-4 w-4" /> {a.status === "incorrect" ? "Corrigir" : "Reativar"}
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1 gap-1.5 rounded-full" onClick={() => openResend(a, true)} data-testid={`act-switch-m-${a.id}`}>
                    <ArrowRightLeft className="h-4 w-4" /> Trocar app
                  </Button>
                </>
              ) : null
            }
          />
        ))}
      </MobileCards>

      <div className="hidden rounded-lg border border-border bg-card md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Aplicativo</TableHead>
              <TableHead>Dado</TableHead>
              <TableHead>Créditos</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Data</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">Nenhuma ativação encontrada.</TableCell></TableRow>
            )}
            {data.items.map((a) => (
              <TableRow key={a.id} data-testid={`act-row-${a.id}`}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
                      {appsMap[a.app_id]?.logo
                        ? <img src={appsMap[a.app_id].logo} alt="" className="h-full w-full object-cover" onError={(e) => (e.target.style.display = "none")} />
                        : <Package className="h-4 w-4 text-muted-foreground" />}
                    </span>
                    <span className="truncate">{a.app_name}</span>
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{a.mac_app}</TableCell>
                <TableCell><span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 font-num text-sm font-semibold">{a.credits_charged}</span></TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <StatusBadge status={a.status} />
                    {a.status === "incorrect" && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-orange-600 dark:text-orange-400" data-testid={`incorrect-${a.id}`}>
                        Corrija o {a.mac_app ? "dado" : "MAC"} (ou troque o app) e reenvie
                      </span>
                    )}
                    {(a.credits_charged || 0) === 0 && (a.credits_cost || 0) > 0 && ["refunded", "failed"].includes(a.status) && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-green-600 dark:text-green-400" data-testid={`refunded-${a.id}`}>
                        <Undo2 className="h-3 w-3" /> Crédito estornado
                      </span>
                    )}
                    {a.refunded_to === "balance" && (a.refunded_amount || 0) > 0 && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-green-600 dark:text-green-400" data-testid={`refunded-balance-${a.id}`}>
                        <Undo2 className="h-3 w-3" /> Reembolsado R$ {Number(a.refunded_amount).toFixed(2)} em saldo
                      </span>
                    )}
                    {(a.credits_charged || 0) > 0 && a.status === "failed" && (
                      <span className="text-[11px] text-muted-foreground" data-testid={`retained-${a.id}`}>Crédito retido — fale com o suporte</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">{new Date(a.created_at).toLocaleString("pt-BR")}</TableCell>
                <TableCell className="text-right">
                  {a.status === "pending" && (
                    <Button variant="outline" size="sm" className="gap-1.5 rounded-full transition-all hover:shadow-sm" disabled={refreshingId === a.id} onClick={() => refreshOne(a.id)} data-testid={`act-refresh-${a.id}`}>
                      <RotateCw className={`h-4 w-4 ${refreshingId === a.id ? "animate-spin" : ""}`} /> Verificar
                    </Button>
                  )}
                  {["incorrect", "failed", "refunded"].includes(a.status) && (
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button size="sm" className="gap-1.5 rounded-full bg-primary/90 transition-all hover:bg-primary hover:shadow-md" onClick={() => openResend(a)} data-testid={`act-retry-${a.id}`}>
                        <RefreshCcw className="h-4 w-4" /> {a.status === "incorrect" ? "Corrigir e reenviar" : "Reativar"}
                      </Button>
                      <Button size="sm" variant="outline" className="gap-1.5 rounded-full" onClick={() => openResend(a, true)} data-testid={`act-switch-${a.id}`}>
                        <ArrowRightLeft className="h-4 w-4" /> Trocar de app
                      </Button>
                    </div>
                  )}
                  {a.status === "approved" && <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {data.total > limit && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{data.total} ativações</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)} data-testid="act-prev">Anterior</Button>
            <Button variant="outline" size="sm" disabled={page * limit >= data.total} onClick={() => setPage((p) => p + 1)} data-testid="act-next">Próxima</Button>
          </div>
        </div>
      )}

      <Dialog open={!!resend} onOpenChange={(o) => !o && setResend(null)}>
        <DialogContent data-testid="resend-dialog">
          <DialogHeader>
            <DialogTitle>Reenviar ativação</DialogTitle>
            <DialogDescription>Corrija o dado ou troque o aplicativo e reenvie. O valor já pago é reaproveitado nesta ativação — só a diferença é ajustada.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1"><ArrowRightLeft className="h-3.5 w-3.5" /> Aplicativo</Label>
              <Popover open={appOpen} onOpenChange={setAppOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="resend-app-select">
                    {selApp ? `${selApp.name} · ${selApp.credit_cost} cr.` : "Selecione o aplicativo"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar aplicativo..." data-testid="resend-app-search" />
                    <CommandList>
                      <CommandEmpty>Nenhum aplicativo encontrado.</CommandEmpty>
                      <CommandGroup>
                        {appsList.map((app) => (
                          <CommandItem key={app.id} value={app.name} onSelect={() => { setSelectedAppId(app.id); setAppOpen(false); }} data-testid={`resend-app-opt-${app.id}`}>
                            <Check className={cn("mr-2 h-4 w-4", selectedAppId === app.id ? "opacity-100" : "opacity-0")} />
                            {app.name} · {app.credit_cost} cr.
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>{macLabel}</Label>
              <Input value={form.mac_app} onChange={(e) => setForm({ ...form, mac_app: selApp?.needs_device_id ? e.target.value : formatMac(e.target.value) })} maxLength={selApp?.needs_device_id ? undefined : 17} placeholder={selApp?.needs_device_id ? "Ex: 1234567890" : "Ex: 49:78:2d:63:87:d9"} data-testid="resend-mac-input" />
            </div>
            {selApp?.needs_mac_key && (
              <div className="space-y-2">
                <Label>Chave (KEY)</Label>
                <Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} data-testid="resend-key-input" />
              </div>
            )}
            <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground" data-testid="resend-cost-hint">
              {isCredits
                ? <>Custo: <span className="font-medium text-foreground">{newVal} crédito(s)</span>.</>
                : <>Valor: <span className="font-medium text-foreground">{money(newVal)}</span>.</>}
              {switching && diff > 0 && (
                <> Diferença de <span className="font-medium text-foreground">+{unitVal(diff)}</span> {isCredits ? "será reservada" : "será debitada do seu saldo"} pela troca.</>
              )}
              {switching && diff < 0 && (
                <> <span className="font-medium text-green-600 dark:text-green-400">Estorno de {unitVal(Math.abs(diff))}</span> {isCredits ? "voltam pra sua carteira" : "voltam pro seu saldo"} pela troca.</>
              )}
              {switching && diff === 0 && <> Troca de app sem alteração de valor.</>}
              {" "}A diferença é ajustada no reenvio; se a ativação falhar, o valor é estornado.
            </div>
            <Button className="w-full gap-2" onClick={submitResend} disabled={sending} data-testid="resend-submit-btn">
              {sending && <Loader2 className="h-4 w-4 animate-spin" />} {switching ? "Trocar app e reenviar" : "Reenviar ativação"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
